import api from '../../../services/api';
import {
  normalizeOrderTransports,
  normalizeSupplierOrderTransports,
  type OrderTransportsDto,
  type SupplierOrderTransportDto,
} from '../../../dto/transport';

/**
 * Every id in this module reaches the URL through here. Ids are opaque to the
 * frontend and can arrive from places the page does not control — the PZ
 * receipt flow, for one, reads a supplier-order id out of the `?supplierOrderId=`
 * query string — so a value containing `/`, `?` or `#` must never be able to
 * re-aim the request at a different API path.
 */
const encodePathSegment = (value: string): string => encodeURIComponent(value);

/**
 * Fetch every transport linked to an order plus the per-product realization
 * breakdown. A single order can be carried on N transports (e.g. a 2-container
 * order delivered on 3 trucks); the response surfaces that fan-out.
 *
 * Transport-level mutations (delete, create, update) live in
 * `src/modules/transports/api/transportRoutesApi.ts` — transports are not
 * owned by an order in the data model, even when surfaced from the order page.
 */
export async function fetchOrderTransports(orderId: string): Promise<OrderTransportsDto> {
  const response = await api.get(`/orders/${encodePathSegment(orderId)}/transports`);
  return normalizeOrderTransports(response.data);
}

/**
 * Etap 2 (transport przywozowy): the inbound sibling of `fetchOrderTransports`
 * — every transport allocated to a SUPPLIER order (ZD), plus the PZ numbers
 * already issued off each one for that ZD. Deliberately a trimmed,
 * non-financial shape (see `SupplierOrderTransportDto`), so no permission gate
 * applies beyond the route's own supplier-order access check.
 *
 * Lives next to its customer-order twin rather than in `supplierOrdersApi`
 * because "which transports carry this order" is one seam, asked from two
 * sides.
 */
export async function fetchSupplierOrderTransports(
  supplierOrderId: string,
): Promise<SupplierOrderTransportDto[]> {
  const response = await api.get(`/supplier-orders/${encodePathSegment(supplierOrderId)}/transports`);
  return normalizeSupplierOrderTransports(response.data, 'supplierOrderTransports');
}

/**
 * Unlink an order from a transport without deleting the transport itself.
 * Transports are N:N, so removing this order leaves the others linked. If the
 * unlinked order was the transport's last one, the backend cascades the
 * delete and returns `{ deleted: true }` so the UI can react (e.g. redirect
 * away from a detail page that just disappeared).
 */
export async function unlinkOrderFromTransport(
  transportId: string,
  orderId: string
): Promise<{ deleted: boolean }> {
  const response = await api.delete(`/transport-routes/${encodePathSegment(transportId)}/orders/${encodePathSegment(orderId)}`);
  return { deleted: Boolean(response.data?.deleted) };
}
